import { ParallaxBanner } from "react-scroll-parallax"
import clsx from "clsx"

import type { SingleImageSlice } from "~/api/prismic/types"
import { SingleImageSizes } from "~/api/prismic/types"
import CustomRichText from "~/components/CustomRichText"
import { useLazyImageRef } from "~/utils/hooks/useLazyImageRef"
import { imageOrFallback } from "~/utils/images/imageOrFallback"
import { buildURL } from "~/utils/urls/buildURL"

type Props = {
  slice: SingleImageSlice
}

const SingleImage: React.FC<Props> = ({ slice }) => {
  const { caption, credit, image, image_size, with_parallax } = slice.primary
  const imageURL = imageOrFallback(image.url)
  const lazyImage = useLazyImageRef(imageURL, {
    imgixParams: {},
    lazySize: { w: 10 },
  })

  const isOriginalSize = image_size === SingleImageSizes.ORIGINAL
  const src = isOriginalSize ? buildURL(imageURL, {}) : lazyImage.url

  const classes = clsx("my-4 mx-auto", {
    "max-w-screen-lg":
      !image_size || image_size === SingleImageSizes.TEXT_COLUMN,
    "w-full": !isOriginalSize,
  })

  return (
    <figure className="my-4 md:my-8">
      {with_parallax ? (
        <ParallaxBanner
          className={classes}
          layers={[
            {
              amount: 0.3,
              image: src,
              props: { ref: lazyImage.ref },
            },
          ]}
        />
      ) : (
        <img
          ref={lazyImage.ref}
          className={classes}
          src={src}
          alt={image.alt || ""}
        />
      )}
      {[caption, credit].some((item) => item?.length) && (
        <figcaption className="flex flex-wrap justify-center space-x-2 px-4 text-center text-sm italic">
          <CustomRichText field={caption} />
          <CustomRichText field={credit} />
        </figcaption>
      )}
    </figure>
  )
}

export default SingleImage
